import { useEffect, useMemo, useState } from "react";
import { Activity, Bot, ChevronRight, Clock, User } from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import type { Building } from "@/lib/api/building";
import type { ActivityCategory } from "@/lib/api/activity";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { encodeInventoryScope } from "@/lib/inventory-scope-codec";
import {
  byWhenDescending,
  loadActivityFeed,
  type NormalizedActivityRow,
} from "@/components/control-tower/activity-feed";
import {
  filterRows,
  emptyStateFor,
  isKindlessOnlySelection,
  actorDisplay,
  subline,
  limitRows,
  feedChromeFor,
  TIME_WINDOWS,
  TIME_WINDOW_LABEL_FI,
  type ActivityEmptyState,
  type TimeWindow,
} from "@/components/control-tower/activity-feed-filters";
import { Chip } from "@/components/control-tower/Chip";
import { EntityKindFilter } from "@/components/control-tower/EntityKindFilter";
import { FilterChipRow } from "@/components/control-tower/FilterChipRow";
import { ENTITY_KIND_FILTER_LABEL } from "@/components/building/findings/findings-helpers";
import { SectionPanel } from "@/components/ui/section-panel";
import { Skeleton } from "@/components/ui/skeleton";
import { cardSubtle, cardTonePill } from "@/lib/card-styles";
import { entityKindForActivityRow, type EntityKind } from "@/lib/entity-kind";
import { cn } from "@/lib/utils";
import { ScopedLink } from "@/lib/scoped-nav";

// Work-class order (§2.2) — not a perspective axis. Strategia carries no event kind yet
// (`CATEGORIES_WITHOUT_KINDS`) but is always offered, never hidden.
const ACTIVITY_WORK_FILTER_LABEL = "Suodata: mihin työhön tämä liittyy?";
const ALL_CATEGORIES: ActivityCategory[] = [
  "Analytiikka",
  "Optimointi",
  "Strategia",
  "Konfiguraatio",
];

// ── Component ────────────────────────────────────────────────────────────────────────────────

interface ActivityFeedProps {
  buildings: Building[];
  // Restricts which category chips are shown/available — defaults to all four Toiminta values.
  // Forwarded server-side to the S4 unified read (story #863).
  categories?: ActivityCategory[];
  // Caps how many rows render, most-recent-first — the final top-N slice after the merged sort.
  // Used by compact/strip embeds (S5, #864); the full `/activity` page omits it.
  limit?: number;
  // Strip/embed chrome: hides the category chips and time-window control (nothing to toggle in a
  // fixed-category, last-N strip) and shows a "Katso koko loki →" link to /activity instead.
  compact?: boolean;
  // Perspective-embed entity-kind default (docs/spec/concepts.md § Shared entity-kind
  // filter — e.g. Datan laatu → `signal`). Seeds the chip's initial selection; the
  // operator can still change it whenever the chip row is shown (i.e. not `compact`). Never a
  // `PerspectiveId` — always one of the entity-kind values themselves.
  defaultEntityKind?: EntityKind;
}

export function ActivityFeed({
  buildings,
  categories,
  limit,
  compact = false,
  defaultEntityKind,
}: ActivityFeedProps) {
  const availableCategories = categories ?? ALL_CATEGORIES;
  const chrome = feedChromeFor(compact);
  const [rows, setRows] = useState<NormalizedActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategories, setActiveCategories] = useState<
    Set<ActivityCategory>
  >(() => new Set(availableCategories));
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("all");
  const [entityKind, setEntityKind] = useState<EntityKind | "all">(
    defaultEntityKind ?? "all",
  );
  // Full-page chevron rows expand in place; a compact/strip embed never has an open row.
  const [openRowKey, setOpenRowKey] = useState<string | null>(null);
  // Building name visibility is keyed off scope spanning more than one building, never a
  // per-row decision (§2.2).
  const { filter, groupId, buildingId } = useInventoryFilter();
  const scope = encodeInventoryScope({ filter, groupId, buildingId });
  const scopeKey = `${scope.building ?? ""}|${scope.group ?? ""}|${scope.city ?? ""}|${scope.type ?? ""}|${scope.status ?? ""}`;
  const showBuildingName = buildings.length > 1;
  const router = useRouter();

  useEffect(() => {
    setRows([]);
    setLoading(true);
    let cancelled = false;
    loadActivityFeed(scope, { categories, limit })
      .then((loadedRows) => {
        if (cancelled) return;
        const sorted = [...loadedRows].sort(byWhenDescending);
        setRows(sorted);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // scopeKey is the stable primitive; `scope` is a fresh object every render
    // (encodeInventoryScope), same as useScopedFindings (#1251).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, categories, limit]);

  const now = Date.now();
  const categoryFiltered = useMemo(
    () => filterRows(rows, activeCategories, timeWindow, now),
    [rows, activeCategories, timeWindow, now],
  );
  // Entity kind composes with — never replaces — the category/time-window filtering above.
  const filtered = useMemo(
    () =>
      entityKind === "all"
        ? categoryFiltered
        : categoryFiltered.filter(
            (row) => entityKindForActivityRow(row) === entityKind,
          ),
    [categoryFiltered, entityKind],
  );
  const limited = useMemo(() => limitRows(filtered, limit), [filtered, limit]);
  const emptyState = emptyStateFor(
    rows.length,
    filtered.length,
    isKindlessOnlySelection(activeCategories),
  );
  const countLabel = loading ? "Ladataan…" : `${filtered.length} tapahtumaa`;

  function toggleCategory(category: ActivityCategory) {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  return (
    <SectionPanel icon={Activity} title="Tapahtumaloki" subtitle={countLabel}>
      {chrome.showCategoryChips && (
        <div className="-mx-4 px-4 pb-2 mb-2 space-y-2 border-b border-border">
          <FilterChipRow label={ACTIVITY_WORK_FILTER_LABEL}>
            {availableCategories.map((category) => (
              <Chip
                key={category}
                active={activeCategories.has(category)}
                label={category}
                onClick={() => toggleCategory(category)}
              />
            ))}
          </FilterChipRow>
          <FilterChipRow
            label={ENTITY_KIND_FILTER_LABEL}
            trailing={
              chrome.showTimeWindowControl ? (
                <>
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {TIME_WINDOWS.map((window) => (
                    <Chip
                      key={window}
                      active={timeWindow === window}
                      label={TIME_WINDOW_LABEL_FI[window]}
                      onClick={() => setTimeWindow(window)}
                    />
                  ))}
                </>
              ) : undefined
            }
          >
            <EntityKindFilter active={entityKind} onChange={setEntityKind} />
          </FilterChipRow>
        </div>
      )}

      {chrome.showTimeWindowControl && !chrome.showCategoryChips && (
        <div className="-mx-4 px-4 pb-2 mb-2 flex items-center justify-end gap-1 border-b border-border">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {TIME_WINDOWS.map((window) => (
            <Chip
              key={window}
              active={timeWindow === window}
              label={TIME_WINDOW_LABEL_FI[window]}
              onClick={() => setTimeWindow(window)}
            />
          ))}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <ActivityFeedSkeleton />
      ) : !loading && emptyState ? (
        <ActivityFeedEmptyState kind={emptyState} />
      ) : (
        <ol className="divide-y divide-border">
          {limited.map((row, i) => {
            const rowKey = `${row.kind}-${row.where.id}-${row.when}-${i}`;
            return (
              <ActivityRowItem
                key={rowKey}
                row={row}
                now={now}
                compact={compact}
                showBuildingName={showBuildingName}
                isOpen={openRowKey === rowKey}
                onToggle={() =>
                  setOpenRowKey((prev) => (prev === rowKey ? null : rowKey))
                }
                onNavigate={() => void router.navigate({ href: row.link })}
              />
            );
          })}
        </ol>
      )}

      {chrome.showViewAllLink && (
        <div className="mt-3 text-right">
          <ScopedLink
            to="/activity"
            className="text-[11px] font-medium text-primary hover:underline"
          >
            Katso koko loki →
          </ScopedLink>
        </div>
      )}
    </SectionPanel>
  );
}

function ActivityFeedSkeleton() {
  return (
    <ol className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-10 w-full" />
        </li>
      ))}
    </ol>
  );
}

function ActivityFeedEmptyState({
  kind,
}: {
  kind: Exclude<ActivityEmptyState, null>;
}) {
  const message =
    kind === "no-activity"
      ? "Ei tapahtumia tällä rajauksella."
      : kind === "not-in-feed-yet"
        ? "Nämä tapahtumatyypit eivät ole vielä mukana listassa."
        : "Ei tapahtumia valituilla suodattimilla.";
  return (
    <p
      className={cn(
        cardSubtle,
        "px-4 py-8 text-center text-xs text-muted-foreground",
      )}
    >
      {message}
    </p>
  );
}

function ActivityRowItem({
  row,
  now,
  compact,
  showBuildingName,
  isOpen,
  onToggle,
  onNavigate,
}: {
  row: NormalizedActivityRow;
  now: number;
  compact: boolean;
  showBuildingName: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const actor = actorDisplay(row.who);
  const ActorIcon = actor.isHuman ? User : Bot;

  // Compact/strip embeds stay non-expanding click-through rows (unchanged behavior) — no
  // chevron, no expanded body, no category chips (§2.2).
  if (compact) {
    return (
      <li className="py-2.5">
        <button
          type="button"
          onClick={onNavigate}
          className="flex w-full items-start gap-2 text-left"
        >
          <ActorIcon
            className={cn(
              "mt-0.5 h-3.5 w-3.5 shrink-0",
              actor.isHuman ? "text-info" : "text-muted-foreground",
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs text-foreground">
              {row.summary}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">
              {subline(row, now, actor.label, showBuildingName)}
            </div>
          </div>
        </button>
      </li>
    );
  }

  return (
    <li className="bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-1 py-2.5 text-left transition-colors hover:bg-surface/60"
      >
        <div className="flex items-start gap-2">
          <ChevronRight
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              isOpen && "rotate-90 text-primary",
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium text-foreground">
              {row.summary}
            </div>
            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {subline(row, now, actor.label, showBuildingName)}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {row.category.map((category) => (
              <span
                key={category}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[10.5px] font-medium",
                  cardTonePill.empty,
                )}
              >
                {category}
              </span>
            ))}
            <ActorIcon
              className={cn(
                "h-3.5 w-3.5",
                actor.isHuman ? "text-info" : "text-muted-foreground",
              )}
            />
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="-mt-1 space-y-2 px-9 pb-3 text-xs">
          {row.detailLines.length > 0 && (
            <ul className="space-y-0.5 text-foreground/90">
              {row.detailLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={onNavigate}
            className="text-[11px] font-medium text-primary hover:underline"
          >
            Avaa {row.avaaLabel} →
          </button>
        </div>
      )}
    </li>
  );
}
